import React from 'react';
import './RecordSequenceNav.css';

// ============================================================================
// SequenceControl — the one prev/next control (Design Constitution N08, O06)
// ----------------------------------------------------------------------------
// Inline '‹ 3 of 12 ›' next to a record title: no fixed edge chevrons, 44px
// steps on the shared pop-out, the target record in each step's aria-label and
// title. Hidden for a list of fewer than two records or a position outside it
// (the single-record hide rule). ←/→ move between records only when focus is
// not in a field or a widget with its own arrow keys, and never while a modal
// dialog is open (O06: Esc and arrows belong to the top-most layer).
// RecordSequenceNav (route-state sequences) and PageHeader's SequenceNav both
// render this, so the behavior and the data-record-seq-* hooks are one.
// ============================================================================

// Arrow keys belong to whatever has focus when it is a field or a widget
// with its own arrow navigation.
export const ARROW_OWNERS =
  'input, textarea, select, [contenteditable="true"], [role="dialog"], [role="listbox"], [role="menu"], [role="tablist"], [role="radiogroup"], [role="group"]';

// Whether a keydown may move the sequence: no modifier, nothing already
// handled it, focus is not in an arrow-owning field/widget, no modal is open.
export function sequenceKeyAllowed(e: KeyboardEvent, doc: Document | null): boolean {
  if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return false;
  const target = e.target as Element | null;
  if (target && typeof target.closest === 'function' && target.closest(ARROW_OWNERS)) return false;
  if (doc && doc.querySelector('[aria-modal="true"]')) return false;
  return true;
}

export function useSequenceKeys(enabled: boolean, onPrev: (() => void) | null, onNext: (() => void) | null): void {
  React.useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (!sequenceKeyAllowed(e, typeof document === 'undefined' ? null : document)) return;
      if (e.key === 'ArrowLeft' && onPrev) {
        e.preventDefault();
        onPrev();
      } else if (e.key === 'ArrowRight' && onNext) {
        e.preventDefault();
        onNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, onPrev, onNext]);
}

export type SequenceControlProps = {
  index: number;
  total: number;
  onPrev?: (() => void) | null;
  onNext?: (() => void) | null;
  prevLabel?: string;
  nextLabel?: string;
};

// The position text, or '' when the control is hidden.
export function sequencePosition(index: number, total: number): string {
  if (!(total >= 2) || index < 0 || index >= total) return '';
  return `${index + 1} of ${total}`;
}

export default function SequenceControl({
  index,
  total,
  onPrev,
  onNext,
  prevLabel = '',
  nextLabel = '',
}: SequenceControlProps) {
  const position = sequencePosition(index, total);
  const prev = onPrev || null;
  const next = onNext || null;
  useSequenceKeys(position !== '', prev, next);
  if (!position) return null;
  return (
    <nav aria-label="Record sequence" data-record-seq-nav="1" className="record-sequence-nav">
      <button
        type="button"
        data-record-seq-prev="1"
        className="wcf-clickable record-sequence-nav__button record-sequence-nav__button--prev"
        disabled={!prev}
        onClick={() => prev?.()}
        aria-label={prev ? 'Previous record: ' + prevLabel : 'No previous record'}
        title={prev ? 'Previous: ' + prevLabel : 'No previous record'}
      >
        <span aria-hidden="true" className="record-sequence-nav__chevron">
          {'‹'}
        </span>
      </button>

      <span data-record-seq-position="1" className="record-sequence-nav__position">
        {position}
      </span>

      <button
        type="button"
        data-record-seq-next="1"
        className="wcf-clickable record-sequence-nav__button record-sequence-nav__button--next"
        disabled={!next}
        onClick={() => next?.()}
        aria-label={next ? 'Next record: ' + nextLabel : 'No next record'}
        title={next ? 'Next: ' + nextLabel : 'No next record'}
      >
        <span aria-hidden="true" className="record-sequence-nav__chevron">
          {'›'}
        </span>
      </button>
    </nav>
  );
}
